import ShopHeader from '@/components/ShopHeader.tsx'
import { Link, useNavigate, useOutlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BasketActionsProvider } from '@/contexts/basketContext.tsx'
import { ProductActionsProvider, useProducts } from '@/contexts/productContext.tsx'
import { Product } from '@/models/shop.ts'
import { Button } from '@/components/Button.tsx'
import { useDrives } from '@/stores/useDrives.ts'
import { IconMessageChatbot, IconPrinter, IconStar } from '@tabler/icons-react'
import AboutUs from '@/components/shop/AboutUs.tsx'
import QualityFooter from '@/components/shop/QualityFooter.tsx'
import { ReactNode, useEffect, useRef, useState } from 'react'
import OurOffer from '@/components/shop/OurOffer.tsx'
import { ExampleImageProvider } from '@/contexts/exampleImageContext.tsx'
import TemplateImageWithExamples from '@/components/TemplateImageWithExamples.tsx'
import { getImageTemplate } from '@/utils/imageTemplateRegistry.ts'
import { HeyDriveType } from '@/models/drive.ts'
import Marquee from 'react-fast-marquee'
import DriveHeader from '@/components/DriveHeader.tsx'
import DriveDeletionElement from '@/components/DriveDeletionElement.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import { useClient } from '@/stores/useClient.ts'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import { ShopBrandProvider, useShopBrand } from '@/contexts/shopBrandContext.tsx'

export default function ShopPage() {
  const outlet = useOutlet()
  const navigate = useNavigate()
  const { currentDrive } = useDrives()
  const { profile, authenticationToken, setProfile, setAuthenticationToken } = useProfile()
  const { createGuestProfile } = useClient()
  const creatingRef = useRef(false)
  const [profileError, setProfileError] = useState<string | null>(null)

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  // Shop hidden for this drive → redirect to main drive page
  useEffect(() => {
    if (!currentDrive?.is_hide_shop) return
    const album = currentDrive.albums.find(a => a.is_primary) || currentDrive.albums[0]
    navigate(`/d/${currentDrive.id}${album ? `/${album.id}` : ''}`, { replace: true })
  }, [currentDrive, navigate])

  useEffect(() => {
    if (profile) return
    if (authenticationToken) return
    if (!currentDrive) return
    const album = currentDrive.albums[0]
    if (!album) return
    if (creatingRef.current) return
    creatingRef.current = true
    createGuestProfile('User', album)
      .then(res => {
        setProfile(res.data)
        setAuthenticationToken(res.headers['ha-token'])
      })
      .catch(e => {
        console.error(e)
        const status = e?.response?.status
        const code = e?.response?.data?.code || e?.code || 'unknown'
        setProfileError(`Profil konnte nicht erstellt werden (${status ?? ''} ${code}). Bitte Seite neu laden.`)
        creatingRef.current = false
      })
  }, [profile, authenticationToken, currentDrive, createGuestProfile, setProfile, setAuthenticationToken])

  if (!profile) {
    if (profileError) {
      return <div className='flex flex-col items-center justify-center min-h-[50vh] gap-4 p-5 text-center'>
        <p>{profileError}</p>
        <Button color='primary' onClick={() => window.location.reload()}>Seite neu laden</Button>
      </div>
    }
    return <LoadBig />
  }

  return <>
    <ShopBrandProvider>
      <BasketActionsProvider>
        <ProductActionsProvider>
          <ExampleImageProvider>
            <style>{`.shop-rect-buttons { --rounded-btn: 0; } .shop-rect-buttons .btn { --rounded-btn: 0; border-radius: 0; } .shop-rect-buttons .shop-product-card, .shop-rect-buttons .shop-product-card img, .shop-rect-buttons .shop-product-card .rounded { border-radius: 0 !important; }`}</style>
            <div className='shop-rect-buttons'>
              <ShopHeader />
              { outlet }
            </div>
          </ExampleImageProvider>
        </ProductActionsProvider>
      </BasketActionsProvider>
    </ShopBrandProvider>
  </>
}

export function ShopPageContent() {
  const { products } = useProducts()
  const { currentDrive } = useDrives()
  const brand = useShopBrand()

  const beforeWeddingProductSkus = ['print-card', 'card-frame']

  const beforeWeddingProducts = products.filter((product) => beforeWeddingProductSkus.includes(product.sku))
  const afterWeddingProducts = products.filter((product) => !beforeWeddingProductSkus.includes(product.sku))

  if (currentDrive?.is_deletion_state) {
    return <div>
      <DriveHeader />
      <DriveDeletionElement drive={currentDrive} />
    </div>
  }

  const afterTitle = brand.isProf ? '' : (currentDrive?.drive_type == HeyDriveType.WEDDING ? 'Nach der Hochzeit' : 'Nach dem Event')

  return <>
    <ShopPageHeader />
    <div className='my-5' />
    <ProductListing products={afterWeddingProducts} title={afterTitle} />
    {!brand.isProf && (
      <ProductListing products={beforeWeddingProducts} title={'Vor der Hochzeit'} />
    )}
    <OurOffer />
    <AboutUs />
    <QualityFooter />
    <Footer />
  </>
}

function ShopPageHeader() {
  function FeatureLabel({ text, icon }: { text: ReactNode, icon: any }) {
    return <div className='flex flex-row gap-3 sm:justify-center items-center'>
      <span className='text-[1.5rem]'>
        { icon }
      </span>
      <span className='text-center'>
        { text }
      </span>
    </div>
  }

  return <>
    <div className='w-full min-h-[25rem] bg-cover bg-no-repeat flex justify-center flex-col' style={{
      backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4392156863), rgba(0, 0, 0, 0.4392156863)), url(/shopAssets/wedding-couple-walking-together.jpeg)',
      backgroundPosition: 'center'
    }}>
      <div className='flex-1 flex justify-center items-center h-full w-full flex-col gap-2 text-center px-5'>
        <h3 className='text-[white] font-sans text-2xl'>Eure Erinnerungen mit unseren Fotoprodukten verewigen</h3>
        <p className='text-[white] font-sans text-lg mt-[1rem]'>Jetzt gestalten und direkt nachhause liefern lassen</p>
        {/*<p className='text-[white] font-sans text-xl'>Fotoabzüge, Kalender, automatische Fotobücher und mehr</p>*/}
      </div>
    </div>
    <div className='py-3 bg-secondary mb-5'>
      <div className='container mx-auto md:hidden'>
        <Marquee className='w-full' speed={20}>
          <div className='flex flex-row justify-between w-full gap-[2rem]'>
            <FeatureLabel text='4,9/5 Sterne auf Trustpilot' icon={<IconStar />} />
            <FeatureLabel text='Made in Germany' icon='🇩🇪' />
            <FeatureLabel text='Individuelle Fotoprodukte' icon={<IconPrinter />} />
            <FeatureLabel text='Persönlicher Support' icon={<IconMessageChatbot />} />
          </div>
        </Marquee>
      </div>
      <div className='container px-5 mx-auto md:grid grid-cols-2 md:grid-cols-4 gap-2 hidden'>
        <FeatureLabel text='4,9/5 Sterne auf Trustpilot' icon={<IconStar />} />
        <FeatureLabel text='Made in Germany' icon='🇩🇪' />
        <FeatureLabel text='Individuelle Fotoprodukte' icon={<IconPrinter />} />
        <FeatureLabel text='Persönlicher Support' icon={<IconMessageChatbot />} />
      </div>
    </div>
  </>
}

function ProductListing({ products, title }: { products: Product[], title: string }) {
  return <div className='container px-4 mx-auto flex flex-col gap-3 items-start relative mt-[1rem]'>
    {title && (
      <h2 className='font-semibold text-2xl mb-3 text-start'>
        {title}
      </h2>
    )}
    <div className='grid grid-cols-2 md:grid-cols-3 gap-3 w-full'>
      {products.map(product => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  </div>
}

function ProductCard({ product }: { product: Product }) {
  const { t } = useTranslation()

  // Calculate the display price (base price or lowest variation price)
  const getDisplayPrice = () => {
    if (product.variations.length === 0) {
      return product.base_price
    }

    const variationPrices = product.variations
      .filter(v => v.price !== null)
      .map(v => parseFloat(v.price!))

    if (variationPrices.length === 0) {
      return product.base_price
    }

    const minVariationPrice = Math.min(...variationPrices)
    const basePrice = parseFloat(product.base_price)

    return Math.min(basePrice, minVariationPrice).toString()
  }

  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  function productContent() {
    const imageTemplate = getImageTemplate(product.image_template_id)

    return <>
      <div className='flex w-full justify-center items-center'>
        {imageTemplate ? (
          <TemplateImageWithExamples
            backgroundImage={product.preview_image || '/placeholder-product.jpg'}
            template={imageTemplate}
            className='rounded w-full aspect-square'
          />
        ) : (
          <img
            className='rounded w-full object-cover aspect-square'
            src={product.preview_image || '/placeholder-product.jpg'}
            alt={product.title}
          />
        )}
      </div>
      <div className='flex gap-1 flex-col items-center h-full p-3 text-center'>
        <h3 className='font-semibold text-lg m-0 w-full mb-auto text-center leading-[1.25rem]'>{product.title}</h3>
        {product.description && (
          <p className='line-clamp-3 text-sm opacity-75 w-full'>{product.description}</p>
        )}
        <div className='flex flex-col'>
          <div className='flex flex-row text-sm opacity-75'>
            <div>{t('shop.from')} {formatPrice(getDisplayPrice())}</div>
          </div>
        </div>
        <div className='flex flex-row justify-center'>
          <Button color='primary' size='sm'>Erstellen</Button>
        </div>
      </div>
    </>
  }

  if (product.external_url) {
    return <a target='_blank' href={product.external_url} className='shop-product-card rounded-xl flex flex-col gap-0 cursor-pointer shadow-xl hover:shadow-md transition-shadow w-full md:max-w-[20rem] overflow-hidden'>
      { productContent() }
    </a>
  }

  return (
    <Link to={`product/${product.sku}`} className='shop-product-card rounded-xl flex flex-col gap-0 cursor-pointer shadow-xl hover:shadow-md transition-shadow w-full md:max-w-[20rem] overflow-hidden'>
      { productContent() }
    </Link>
  )
}

function Footer() {
  const brand = useShopBrand()
  return <footer className="w-full py-5 pb-[2rem]">
    <div className="container px-4 mx-auto">
      <div className="flex gap-4 flex-col lg:flex-row">
        <div className="flex flex-1 flex-col">
          <a className="font-logo text-2xl" href="/">{brand.name}</a>
        </div>

        <div className="flex-1">
          <h4 className="font-sans uppercase font-semibold mb-2">
            Unternehmen </h4>
          <div className="menu-business-container">
            <div className="mb-2 menu-item menu-item-type-post_type menu-item-object-page"><a
              href="https://fridaysnap.de/ueber-uns/">Über Uns</a></div>
            <div className="mb-2 menu-item menu-item-type-post_type menu-item-object-page"><a
              href="https://fridaysnap.de/faq/">FAQ</a></div>
            <div className="mb-2 menu-item menu-item-type-custom menu-item-object-custom"><a
              href="https://de.trustpilot.com/review/heyalbum.com">Trustpilot</a></div>
            <div className="mb-2 menu-item menu-item-type-taxonomy menu-item-object-category"><a
              href="https://fridaysnap.de/kategorie/hochzeit/">Beiträge: Hochzeit</a></div>
            <div className="mb-2 menu-item menu-item-type-taxonomy menu-item-object-category"><a
              href="https://fridaysnap.de/kategorie/magazin/">Weitere Beiträge</a></div>
            <div className="mb-2 menu-item menu-item-type-post_type menu-item-object-page"><a
              href="https://fridaysnap.de/kostenlose-kartendesign-vorlagen-fuer-qr-code-hochzeitsfotos/">Kostenlose
              Kartendesigns</a></div>
            <div className="mb-2 menu-item menu-item-type-post_type menu-item-object-page"><a
              href="https://fridaysnap.de/gutschein/">Gutschein schenken</a></div>
          </div>
        </div>

        <div className="flex-1">
          <h4 className="font-sans uppercase font-semibold mb-2">
            Socials
          </h4>
          <div className="menu-socials-container">
            <div className="mb-2 menu-item menu-item-type-custom menu-item-object-custom"><a
              href="https://www.instagram.com/fridaysnap.de/">Instagram</a></div>
            <div className="mb-2 menu-item menu-item-type-custom menu-item-object-custom"><a
              href="https://www.tiktok.com/@fridaysnap_official">TikTok</a></div>
            <div className="mb-2 menu-item menu-item-type-custom menu-item-object-custom"><a
              href="https://www.pinterest.de/FridaySnap">Pinterest</a></div>
            <div className="mb-2 menu-item menu-item-type-custom menu-item-object-custom"><a
              href="https://www.facebook.com/fridaysnap/">Facebook</a></div>
          </div>
        </div>
      </div>

      <div className="flex flex-col mt-5 gap-2 pb-5">
        <h4 className="font-sans uppercase font-semibold mb-2">
          Bezahlmethoden
        </h4>
        <img className='max-w-[20rem]' src="/shopAssets/payment-methods-more.webp" />
      </div>

      <div className="flex flex-col mt-5 text-neutral-400">
        <a href="https://fridaysnap.de/impressum/">Impressum</a>
        <a href="https://fridaysnap.de/widerrufsbelehrung/">Widerrufsbelehrung</a>
        <a href="https://fridaysnap.de/datenschutzerklaerung//">Datenschutzerklärung</a>
      </div>

      <div className="mt-5 text-sm">
        <p className="mb-1">Designed with ❤️ in Frankfurt am Main</p>
        <span>© {brand.name} {new Date().getFullYear()}</span>
      </div>
    </div>
  </footer>
}

// function OrderItemElement({ orderItem}: { orderItem: OrderItem }) {
//   const { t } = useTranslation()
//   const navigate = useNavigate()
//   const { getProduct } = useShopProducts()
//   const {getConfiguration} = useProductConfiguration()
//   function onOrderItemClick() {
//     navigate(`item/${orderItem.id}`)
//   }
//   const configuration = getConfiguration(orderItem.kind)!
//   const product = getProduct(orderItem.kind)
//   const price = calculateTotalPrice(configuration, orderItem as unknown as Record<string, string>)
//   const statusText = {
//     [OrderItemStatus.IN_DESIGN]: t('shop.statusSelectingPhotos'),
//     [OrderItemStatus.IN_CHECK]: t('shop.statusCheckingDesign'),
//     [OrderItemStatus.IN_PROGRESS]: t('shop.statusProcessingDesign'),
//     [OrderItemStatus.DONE]: t('shop.statusDone'),
//   }[orderItem.order_item_status]
//
//   return <div className='bg-secondary p-2 rounded flex flex-col gap-2 shadow cursor-pointer' onClick={onOrderItemClick}>
//     <img className='rounded shadow' src={product?.imgSrc} alt=""/>
//     <div className='flex flex-row items-center mt-auto'>
//       <div className='flex flex-col'>
//         <h3 className='font-bold'>{product?.title}</h3>
//         <div className='flex flex-row'>
//           <div className='text-sm'>{formatPrice(price)}</div>
//         </div>
//         <div className='flex flex-row text-sm mt-1'>
//           {statusText}
//         </div>
//       </div>
//       <div className='ms-auto'>
//         <IconChevronRight />
//       </div>
//     </div>
//   </div>
// }
//
// function ShopProduct({ orderKind, productId, title, description, imgSrc, minPrice, externalLink }: { orderKind: OrderItemKind, productId: string, title: string, description?: string, imgSrc?: string, minPrice?: string, externalLink?: string }) {
//   const { t } = useTranslation()
//   const navigate = useNavigate()
//   const { currentDrive } = useDrives()
//
//   function onProductClick() {
//     if (orderKind != OrderItemKind.EXTERNAL_LINK && orderKind != OrderItemKind.INTERNAL_LINK) {
//       navigate(productId)
//     }
//   }
//
//   if (orderKind === OrderItemKind.INTERNAL_LINK) {
//     return <Link to={`/d/${currentDrive!.id}/${externalLink}`}>
//       {productContent()}
//     </Link>
//   }
//
//   if (externalLink) {
//     return <a target='_blank' href={externalLink}>
//       {productContent()}
//     </a>
//   }
//
//   return productContent()
//
//   function productContent() {
//     return <div className='rounded flex flex-row gap-2 cursor-pointer h-full' onClick={onProductClick}>
//       <div className='flex max-w-[40%] justify-center items-center bg-secondary rounded p-2'>
//         <img className='rounded shadow w-full' src={imgSrc} alt=""/>
//       </div>
//       <div className='flex flex-col mt-auto flex-1 p-2 h-full '>
//         <h3 className='font-bold text-lg'>{title}</h3>
//         { description && <p className='line-limit-3 text-sm'>{description}</p> }
//         <div className='flex flex-col mt-2'>
//           { minPrice && <div className='flex flex-row'>
//             <div>{t('shop.from')} {minPrice}</div>
//           </div>
//           }
//         </div>
//         <div className='flex flex-row mt-2 justify-center'>
//           <Button color='primary' size='sm'>{t('shop.configure')}</Button>
//           <div className='ms-auto'>
//             <IconChevronRight />
//           </div>
//         </div>
//       </div>
//     </div>
//   }
// }
